import { useCallback, useEffect, useRef, useState } from 'react';
import { apiGet } from '../client';
import type { AccountData, AccountResponse } from '@/@types/account';

/**
 * React hook to fetch current user's account data
 * Uses abort controller to cancel in-flight requests
 */
export function useAccountData() {
    const [data, setData] = useState<AccountData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const ctrlRef = useRef<AbortController | null>(null);

    const refetch = useCallback(async () => {
        // Cancel previous in-flight request
        ctrlRef.current?.abort();
        const ctrl = new AbortController();
        ctrlRef.current = ctrl;

        setLoading(true);
        setError(null);

        try {
            const response = await apiGet<AccountResponse>('/app/v1/account');
            setData(response.user);
        } catch (e: any) {
            if (e?.name === 'AbortError') return;
            setError(e?.message || 'Failed to load account data');
        } finally {
            if (ctrlRef.current === ctrl) ctrlRef.current = null;
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void refetch();
        return () => ctrlRef.current?.abort();
    }, [refetch]);

    return { data, loading, error, refetch };
}

